<!-- Every cited item renders its evidence through this one template, on one line each (RF-21). -->
<ng-template #evidence let-items>
  @for (item of items; track $index) {
    <small class="text-muted d-block text-truncate">“{{ item.quote }}” · {{ when(item) }}</small>
  }
</ng-template>

<ng-template #nothing>
  <p class="text-muted small mb-0">{{ "attendance.copilot.insight.nothingToReport" | translate }}</p>
</ng-template>

<!-- A list of notes: a text with its evidence, or the line that says there is nothing. -->
<ng-template #notes let-items>
  <ul class="list-unstyled d-flex flex-column gap-2 mb-0">
    @for (note of items; track $index) {
      <li>
        <p class="mb-0">{{ note.text }}</p>
        <ng-container *ngTemplateOutlet="evidence; context: { $implicit: note.evidence }" />
      </li>
    } @empty {
      <li><ng-container *ngTemplateOutlet="nothing" /></li>
    }
  </ul>
</ng-template>

<div class="insight-accordion">
  <!-- 1. Situation now -->
  <button
    type="button"
    class="insight-accordion__toggle insight-accordion__toggle--first"
    [attr.aria-expanded]="isOpen('situation')"
    (click)="toggle('situation')"
  >
    <span class="insight-accordion__title">{{ "attendance.copilot.insight.section.situation" | translate }}</span>
    <span class="insight-accordion__meta"><i [class]="chevron('situation')"></i></span>
  </button>
  @if (isOpen("situation")) {
    <div class="insight-accordion__panel">
      <p class="mb-0">{{ content.situationNow }}</p>
    </div>
  }

  <!-- 2. Open questions -->
  <button type="button" class="insight-accordion__toggle" [attr.aria-expanded]="isOpen('questions')" (click)="toggle('questions')">
    <span class="insight-accordion__title">{{ "attendance.copilot.insight.section.openQuestions" | translate }}</span>
    <span class="insight-accordion__meta">
      <span class="badge campaign-badge" [ngClass]="countTone(content.openQuestions.length)">{{ content.openQuestions.length }}</span>
      <i [class]="chevron('questions')"></i>
    </span>
  </button>
  @if (isOpen("questions")) {
    <div class="insight-accordion__panel">
      <ng-container *ngTemplateOutlet="notes; context: { $implicit: content.openQuestions }" />
    </div>
  }

  <!-- 3. What the customer needs -->
  <button type="button" class="insight-accordion__toggle" [attr.aria-expanded]="isOpen('needs')" (click)="toggle('needs')">
    <span class="insight-accordion__title">{{ "attendance.copilot.insight.section.customerNeeds" | translate }}</span>
    <span class="insight-accordion__meta">
      <span class="insight-accordion__meta-text">
        {{ "attendance.copilot.insight.section.intent" | translate }} ·
        {{ pluralKey("implicitNeeds", content.customerNeeds.implicitNeeds.length) | translate: { count: content.customerNeeds.implicitNeeds.length } }} ·
        {{ pluralKey("pains", content.customerNeeds.pains.length) | translate: { count: content.customerNeeds.pains.length } }}
      </span>
      <i [class]="chevron('needs')"></i>
    </span>
  </button>
  @if (isOpen("needs")) {
    <div class="insight-accordion__panel">
      <dl class="d-flex flex-column gap-2 mb-0">
        <div>
          <dt class="small text-muted">{{ "attendance.copilot.insight.section.intent" | translate }}</dt>
          <dd class="mb-0">
            <p class="mb-0">{{ content.customerNeeds.intent.text }}</p>
            <ng-container *ngTemplateOutlet="evidence; context: { $implicit: content.customerNeeds.intent.evidence }" />
          </dd>
        </div>
        <div>
          <dt class="small text-muted">{{ "attendance.copilot.insight.section.implicitNeeds" | translate }}</dt>
          <dd class="mb-0"><ng-container *ngTemplateOutlet="notes; context: { $implicit: content.customerNeeds.implicitNeeds }" /></dd>
        </div>
        <div>
          <dt class="small text-muted">{{ "attendance.copilot.insight.section.pains" | translate }}</dt>
          <dd class="mb-0"><ng-container *ngTemplateOutlet="notes; context: { $implicit: content.customerNeeds.pains }" /></dd>
        </div>
      </dl>
    </div>
  }

  <!-- 4. Relationship -->
  <button type="button" class="insight-accordion__toggle" [attr.aria-expanded]="isOpen('relationship')" (click)="toggle('relationship')">
    <span class="insight-accordion__title">{{ "attendance.copilot.insight.section.relationship" | translate }}</span>
    <span class="insight-accordion__meta">
      <span class="insight-accordion__meta-text">
        {{ "attendance.copilot.insight.value." + content.relationship.sentiment.value | translate }} ·
        {{ riskMetaKey(content.relationship.churnRisk.level) | translate }} ·
        {{ content.relationship.health.score }}/100
      </span>
      <i [class]="chevron('relationship')"></i>
    </span>
  </button>
  @if (isOpen("relationship")) {
    <div class="insight-accordion__panel">
      <dl class="d-flex flex-column gap-2 mb-0">
        <div>
          <dt class="small text-muted">
            {{ "attendance.copilot.insight.section.sentiment" | translate }}:
            {{ "attendance.copilot.insight.value." + content.relationship.sentiment.value | translate | lowercase }},
            {{ "attendance.copilot.insight.value." + content.relationship.sentiment.trend | translate | lowercase }}
          </dt>
          <dd class="mb-0">
            <p class="mb-0">{{ content.relationship.sentiment.reason }}</p>
            <ng-container *ngTemplateOutlet="evidence; context: { $implicit: content.relationship.sentiment.evidence }" />
          </dd>
        </div>
        <div>
          <dt class="small text-muted">
            {{ "attendance.copilot.insight.section.churnRisk" | translate }}:
            {{ "attendance.copilot.insight.value." + content.relationship.churnRisk.level | translate | lowercase }}
          </dt>
          <dd class="mb-0"><ng-container *ngTemplateOutlet="notes; context: { $implicit: content.relationship.churnRisk.signals }" /></dd>
        </div>
        <div>
          <dt class="small text-muted">
            {{ "attendance.copilot.insight.section.health" | translate }}: {{ content.relationship.health.score }}/100,
            {{ "attendance.copilot.insight.value." + content.relationship.health.band | translate | lowercase }}
          </dt>
          <dd class="mb-0">
            <div
              class="progress insight-health-bar mb-1"
              role="progressbar"
              [attr.aria-label]="'attendance.copilot.insight.section.health' | translate"
              [attr.aria-valuenow]="content.relationship.health.score"
              aria-valuemin="0"
              aria-valuemax="100"
            >
              <div class="progress-bar" [ngClass]="healthBar(content.relationship.health.band)" [style.width.%]="content.relationship.health.score"></div>
            </div>
            <p class="mb-0">{{ content.relationship.health.reason }}</p>
            <ng-container *ngTemplateOutlet="evidence; context: { $implicit: content.relationship.health.evidence }" />
          </dd>
        </div>
      </dl>
    </div>
  }

  <!-- 5. Attention points -->
  <button type="button" class="insight-accordion__toggle" [attr.aria-expanded]="isOpen('attention')" (click)="toggle('attention')">
    <span class="insight-accordion__title">{{ "attendance.copilot.insight.section.attentionPoints" | translate }}</span>
    <span class="insight-accordion__meta">
      <span class="badge campaign-badge" [ngClass]="countTone(content.attentionPoints.length)">{{ content.attentionPoints.length }}</span>
      <i [class]="chevron('attention')"></i>
    </span>
  </button>
  @if (isOpen("attention")) {
    <div class="insight-accordion__panel">
      <ng-container *ngTemplateOutlet="notes; context: { $implicit: content.attentionPoints }" />
    </div>
  }

  <!-- 6. Relationship opportunities -->
  <button type="button" class="insight-accordion__toggle" [attr.aria-expanded]="isOpen('opportunities')" (click)="toggle('opportunities')">
    <span class="insight-accordion__title">{{ "attendance.copilot.insight.section.opportunities" | translate }}</span>
    <span class="insight-accordion__meta">
      @if (content.opportunities[0]; as first) {
        <span class="campaign-chip is-brand">{{ "attendance.copilot.insight.value." + first.type | translate }}</span>
        @if (moreOpportunities() > 0) {
          <span class="campaign-chip">+{{ moreOpportunities() }}</span>
        }
      } @else {
        <span class="badge campaign-badge is-neutral">0</span>
      }
      <i [class]="chevron('opportunities')"></i>
    </span>
  </button>
  @if (isOpen("opportunities")) {
    <div class="insight-accordion__panel">
      <ul class="list-unstyled d-flex flex-column gap-2 mb-0">
        @for (opportunity of content.opportunities; track $index) {
          <li>
            <p class="mb-0">{{ opportunity.action }}</p>
            <ng-container *ngTemplateOutlet="evidence; context: { $implicit: opportunity.evidence }" />
          </li>
        } @empty {
          <li><ng-container *ngTemplateOutlet="nothing" /></li>
        }
      </ul>
    </div>
  }

  <!-- 7. What has happened so far -->
  <button type="button" class="insight-accordion__toggle" [attr.aria-expanded]="isOpen('facts')" (click)="toggle('facts')">
    <span class="insight-accordion__title">{{ "attendance.copilot.insight.section.facts" | translate }}</span>
    <span class="insight-accordion__meta">
      <span class="insight-accordion__meta-text">{{ pluralKey("facts", content.facts.length) | translate: { count: content.facts.length } }}</span>
      <i [class]="chevron('facts')"></i>
    </span>
  </button>
  @if (isOpen("facts")) {
    <div class="insight-accordion__panel">
      <ol class="list-unstyled d-flex flex-column gap-1 mb-0">
        @for (fact of content.facts; track $index) {
          <li class="d-flex align-items-start gap-2">
            <span class="insight-fact-dot" [ngClass]="factDot(fact.kind)" role="img" [attr.aria-label]="'attendance.copilot.insight.value.' + fact.kind | translate"></span>
            <div class="flex-grow-1 min-w-0 d-flex flex-column">
              <span class="d-flex justify-content-between gap-2 fw-medium text-dark">
                <span>{{ fact.title }}</span>
                <small class="text-muted text-nowrap fw-normal">{{ factTime(fact) }}</small>
              </span>
              <small>{{ fact.detail }}</small>
            </div>
          </li>
        } @empty {
          <li><ng-container *ngTemplateOutlet="nothing" /></li>
        }
      </ol>
    </div>
  }
</div>
